import { useCallback, useEffect, useState } from 'react';
import checkFeatureFlagByName from '../api/feature-flags/checkFeatureFlagByName';

/**
 * Flag que abre la solicitud de rescates. Se resuelve por `user_ids` en la
 * tabla `feature_flags`, así que se puede abrir a una sola persona.
 */
export const RESCUES_FEATURE_FLAG = 'rescues-request';

export interface UseRescuesEnabledResult {
	enabled: boolean;
	loading: boolean;
}

/**
 * ¿Puede esta persona pedir un rescate?
 *
 * Mientras Caser no nos dé por API quién tiene algo rescatable por antigüedad
 * (+10 años), abrirlo a todo el mundo generaría solicitudes que no sabemos
 * resolver — y peor: alguien que ni siquiera puede rescatar todavía pediría
 * por jubilación "a ver qué pasa", y eso obliga a contestarle a mano y a
 * desmontarle la expectativa. Los otros 6 motivos no dependen de ese dato,
 * pero el problema es el mismo: exponer la puerta a quien no le toca.
 *
 * Por eso arranca cerrado y se abre caso a caso. **Ante cualquier duda,
 * apagado**: sin token, sin usuario, con la petición fallando o con el flag
 * sin crear (el backend devuelve `isActive: false` si no existe), no se ve
 * nada de rescates.
 */
const useRescuesEnabled = (
	token: string | null | undefined,
	companyId: string | null | undefined,
	userId: string | null | undefined
): UseRescuesEnabledResult => {
	const [enabled, setEnabled] = useState(false);
	const [loading, setLoading] = useState(false);

	const check = useCallback(async () => {
		if (!token || !userId) {
			setEnabled(false);
			return;
		}

		setLoading(true);

		try {
			const res = await checkFeatureFlagByName(
				token,
				companyId ?? '',
				userId,
				RESCUES_FEATURE_FLAG
			);
			setEnabled(!!(res?.success && res.data?.isActive === true));
		} catch {
			// Un fallo de red no puede abrir la feature a todo el mundo.
			setEnabled(false);
		} finally {
			setLoading(false);
		}
	}, [token, companyId, userId]);

	useEffect(() => {
		check();
	}, [check]);

	return { enabled, loading };
};

export default useRescuesEnabled;
